import * as t from 'io-ts';

import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {colorForCompanyId} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {parseCompanyPreferences, RCompanyPreferences} from './companyPreferencesModel';

/*
 * Runtime type.
 */

export const defaultCompanyName = '[Unknown Company Name]';

export const pastDueInvoiceString = 'past_due_invoices';

export const recentChargeFailedString = 'recent_charge_failed';

export enum CompanyTrialPlansEnum {
  TRIAL = 'trial',
  FREE = 'free',
  MANAGED_TRIAL_PRIME = 'managed-trial-prime',
  MANAGED_TRIAL_ENTERPRISE = 'managed-trial-enterprise',
  MANAGED_TRIAL_GROWTH = 'managed-trial-growth',
  MANAGED_TRIAL_SCALE = 'managed-trial-scale',
}
export const isCompanyTrialPlan = makeEnumTypeGuard(CompanyTrialPlansEnum);

export enum CompanyFeaturesEnum {
  ACCOUNT_RULES = 'account_rules',
  API = 'api',
  ANALYTICS = 'analytics',
  ACCOUNT_ANALYTICS = 'account_analytics',
  ADVANCED_ANALYTICS = 'advanced_analytics',
  RULES = 'rules',
  ROLES = 'roles',
  SEQUENCES = 'sequences',
  SUPPORT_CHAT = 'support-chat',
  TEAMS = 'teams',
  ADVANCED_RULES = 'advanced_rules',
  GLOBAL_RULES = 'global_rules',
  UPGRADED_RULES = 'upgraded_rules',
  SLA_RULES = 'sla_rules',
  SLA_RULES_TRIAL = 'sla_rules:trial',
  SHIFTS = 'shifts',
  SURVEYS = 'surveys',
  CUSTOM_VIEWS = 'custom_views',
  CUSTOM_VIEWS_MANY = 'custom_views:+',
  CUSTOM_VIEWS_TRIAL = 'custom_views:trial',
  REQUIRED_TAGGING_RULES = 'required_tagging_rules',
  SMART_RULES = 'smart_rules',
  TABLE_BASED_RULES = 'table_based_rules',
  CRM_SYNC = 'crm_sync',
}
export const isCompanyFeaturesEnum = makeEnumTypeGuard(CompanyFeaturesEnum);

export enum CompanyStatusesEnum {
  OK = 'ok',
  BLOCKED = 'blocked',
  BLOCKED_BILLING = 'blocked_billing',
}

export const RCompanyFeatures = makeEnumRuntimeType(CompanyFeaturesEnum);

const RCompanyDirty = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    name: t.string,
    plan: t.union([t.string, t.undefined]),
    region: t.union([t.string, t.undefined]),
    shard: t.union([t.string, t.undefined]),
    cell: t.union([t.string, t.undefined]),
    features: t.readonlyArray(t.string),
    subdomain: t.string,
    preferences: RCompanyPreferences,
    color: makeEnumRuntimeType(PaletteColorsEnum),
    isInTrial: t.boolean,
    isInManagedTrial: t.boolean,
    trialEndDate: t.union([t.Integer, t.undefined]),
    hasStripeId: t.boolean,
    status: t.union([t.string, t.undefined]),
    stripeError: t.union([t.string, t.undefined]),
    discountEndDate: t.union([t.Integer, t.undefined]),
    createdAt: t.Integer,
    updatedAt: t.Integer,
    isBilledThroughOtherCompany: t.boolean,
  }),
  'company',
);

/*
 * Type.
 */

export interface CompanyModel extends t.TypeOf<typeof RCompanyDirty> {}
export const RCompany = t.clean<CompanyModel>(RCompanyDirty as any);

/*
 * IO methods.
 */

export function parseCompany(src: any): CompanyModel {
  return {
    id: src.id,
    url: src.url,
    name: src.name,
    subdomain: src.subdomain,
    plan: src.plan || undefined,
    region: src.region || undefined,
    shard: src.shard || undefined,
    cell: src.cell || undefined,
    features: src.features || [],
    preferences: parseCompanyPreferences(src.preferences),
    color: colorForCompanyId(src.id),
    isInTrial: src.is_trial || false,
    isInManagedTrial: src.is_managed_trial || false,
    trialEndDate: src.trial_end || undefined,
    hasStripeId: Boolean(src.has_stripe_id),
    status: src.status || undefined,
    stripeError: src.stripe_error || undefined,
    discountEndDate: src.discount_end || undefined,
    createdAt: src.created_at,
    updatedAt: src.updated_at,
    isBilledThroughOtherCompany: Boolean(src.billed_through_other_company),
  };
}

/*
 * API type.
 */

export const ApiCompany = makeApiType('company', RCompany, parseCompany);
